import { ReactNode, useEffect } from 'react'

type Size = 'sm' | 'md' | 'lg' | 'xl' | 'full'

const sizes: Record<Size, string> = {
  sm: '440px',
  md: '640px',
  lg: '840px',
  xl: '1040px',
  full: '1400px',
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
}: {
  open: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  size?: Size
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const isFull = size === 'full'

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 400,
        padding: isFull ? '16px' : '48px 24px',
        overflow: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-bg-elevated)',
          borderRadius: 'var(--radius-modal)',
          boxShadow: 'var(--shadow-card)',
          width: '100%',
          maxWidth: sizes[size],
          display: 'flex',
          flexDirection: 'column',
          maxHeight: isFull ? 'calc(100vh - 32px)' : 'calc(100vh - 96px)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 28px',
            borderBottom: '1px solid var(--color-hairline)',
          }}
        >
          <h3
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: '18px',
              fontWeight: 600,
              margin: 0,
              letterSpacing: '-0.011em',
            }}
          >
            {title}
          </h3>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-tertiary)',
              fontSize: '22px',
              lineHeight: 1,
              cursor: 'pointer',
              padding: '4px 8px',
              fontFamily: 'inherit',
            }}
          >
            ×
          </button>
        </div>
        <div
          style={{
            padding: isFull ? '0' : '24px 28px',
            flex: 1,
            overflowY: 'auto',
          }}
        >
          {children}
        </div>
        {footer && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              padding: '16px 28px',
              borderTop: '1px solid var(--color-hairline)',
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
